import {GetGtaRateListRequest, GtaRate, GtaRateStatusEnum} from "@/Stub/V1";
import {Column, ColumnBodyOptions, ColumnEditorOptions, ColumnEvent} from "primereact/column";
import {DataTable, DataTablePassThroughOptions, DataTableStateEvent} from "primereact/datatable";
import {useNavigate} from "react-router-dom";
import styled from "styled-components";
import React, {useMemo} from "react";
import {url} from "@/Helpers/general";
import {Button} from "primereact/button";
import {useUpdateSearchParams} from "@/Hooks/useUpdateSearchParams";
import {useGetGtaRates} from "@/Service/Api/V1/ApiHooks/GtaRates/useGetGtaRates";
import {Tag} from "primereact/tag";
import BaseNumberInput from "@/Components/Forms/Form/BaseNumberInput";
import {Chip} from "primereact/chip";
import {useSortGtaRate} from "@/Service/Api/V1/ApiHooks/GtaRates/useSortGtaRate";
import {QueryKeys} from "@/Service/Api/V1/QueryKeys/QueryKeys";
import {useQueryClient} from "@tanstack/react-query";
import {useToastMessagesStore} from "@/Stores/ToastMessagesStore";
import useGetGtaRatesFiltersQuery, {GtaRatesFilterType} from "@/Hooks/useGetGtaRatesFiltersQuery";


type TableColumnDefinition = {
    label?: string
    sortable?: boolean
    field?: string
    sortField?: string
    headerTooltip?: string
    editor?: React.ReactNode | ((options: ColumnEditorOptions) => React.ReactNode)
    onCellEditComplete?: (event: ColumnEvent) => void
    body?: React.ReactNode | ((data: GtaRate, options: ColumnBodyOptions) => React.ReactNode)
};

const dataTablePtOptions: DataTablePassThroughOptions = {
    root: {
        className: 'datatable-base'
    }
};

const StyledWrap = styled.main`
    .content-header {
        padding: 1rem;
        border-bottom: var(--gray-300) 1px solid;

        &__title {
            margin: 0.25rem 0;
            font-weight: 600;
            font-size: 1.5rem;
        }

        &__description {
            font-size: 0.9rem;
            color: var(--gray-500);
        }
    }

    .content-container {
        margin-top: 2rem;
        padding: 0 1rem;
    }

    .datatable-container {
        margin-top: 2rem;
        font-size: 0.8rem;
    }

    .datatable-base {
        font-size: 0.8rem;
        font-weight: 500;
    }

    .datatable-cell {
        &__clean-text {
            margin: 0;
        }
    }

    .angles-sort {
        margin-left: 0.3rem;

        &__icon {
            color: var(--primary-200);
            font-size: 0.8rem;
            font-weight: 600;

            &.active {
                color: var(--primary-500);
            }
        }
    }
`;


export type GtaRatesDatatableProps = {
    requestParams?: Partial<GetGtaRateListRequest>
};


const GtaRatesDatatable = ({requestParams}: GtaRatesDatatableProps) => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const addToastMessage = useToastMessagesStore((state) => state.addToastMessage);

    const {
        pageQuery,
        pageSizeQuery
    } = useGetGtaRatesFiltersQuery();

    const updateGtaRatesFilterHandler = useUpdateSearchParams();

    const gtaRatesRequest = useMemo<GetGtaRateListRequest>(() => {
        return {
            ...requestParams,
            page: pageQuery ? parseInt(pageQuery) : 1,
            per_page: pageSizeQuery ? parseInt(pageSizeQuery) : 10,
        };
    }, [requestParams, pageQuery, pageSizeQuery]);

    const {
        data: gtaRatesData,
        isLoading: gtaRatesLoading
    } = useGetGtaRates({
        requestParams: gtaRatesRequest
    });

    const {mutateAsync: sortGtaRate, isPending: sortIsLoading} = useSortGtaRate();

    const navigateToGtaRate = (gtaRate: GtaRate) => {
        navigate(url('gta-rate', {gtaRateId: gtaRate.id}));
    };

    const actionsBodyTemplate = (gtaRate: GtaRate) => {
        return (
            <div className="flex gap-2">
                <Button
                    className={'p-button-primary'}
                    onClick={() => navigateToGtaRate(gtaRate)}
                    label='Details'
                />
            </div>
        );
    };

    const onCellEditComplete = async (e: ColumnEvent) => {
        let {rowData, newValue, value} = e;

        if (newValue === value) return;

        await sortGtaRate({
            gta_id: rowData.id,
            SortGtaRequest: {
                gta_sort_index: newValue
            }
        }, {
            onSuccess: async () => {
                await queryClient.invalidateQueries({
                    queryKey: QueryKeys.gtaRates._def
                });
                addToastMessage({
                    severity: 'success',
                    summary: 'GTA Rate updated',
                    detail: 'GTA Rate has been updated successfully',
                });
            }
        })
    };

    const tableColumns: TableColumnDefinition[] = [
        {
            label: 'GTA Group Code',
            field: 'gta_group'
        },
        {
            label: 'Daily Hire Rate',
            field: 'daily_hire_rate'
        },
        {
            label: 'Sort Index',
            field: 'gta_sort_index',
            body: (gtaRate: GtaRate) => <div className={'w-xl'}><Chip
                className={'flex-row-reverse justify-content-center gap-3'}
                label={(gtaRate.gta_sort_index ?? 'N/A') + ''}
                icon={'pi pi-pencil'}/></div>,
            editor: (options) => <BaseNumberInput className={'w-full'} inputClassName={'text-center'}
                                                  value={options.value}
                                                  maxFractionDigits={0}
                                                  onChange={(e) => options.editorCallback(e.value)}
                                                  min={1}
                                                  step={1}
                                                  showButtons
            />,
            onCellEditComplete: onCellEditComplete
        },
        {
            label: 'Status',
            field: 'status',
            body: (gtaRate) => {
                return <Tag
                    rounded
                    value={gtaRate.status}
                    severity={gtaRate.status === GtaRateStatusEnum.Active ? 'success' : 'danger'
                    }/>;
            }
        },
        {
            label: 'GTA Group Description',
            field: 'description',
        },
        {
            label: 'Actions',
            body: actionsBodyTemplate
        }
    ];

    const onPagination = (e: DataTableStateEvent) => {
        updateGtaRatesFilterHandler(GtaRatesFilterType.Page, ((e.first / e.rows) + 1).toString());
        updateGtaRatesFilterHandler(GtaRatesFilterType.PageSize, e.rows.toString());
    };

    const first = pageSizeQuery && pageQuery ? Number(pageSizeQuery) * Number(pageQuery) - 10 : 0;
    const rows = pageSizeQuery ? Number(pageSizeQuery) : 10;

    return (
        <StyledWrap>
            <div className="content-container">
                <DataTable
                    lazy
                    loading={gtaRatesLoading || sortIsLoading}
                    emptyMessage="No GTA rates found."
                    value={gtaRatesData?.data}
                    totalRecords={gtaRatesData?.meta.total}
                    paginator
                    editMode={'cell'}
                    rows={rows}
                    rowsPerPageOptions={[10, 20, 50]}
                    first={first}
                    pt={dataTablePtOptions}
                    onPage={e => {
                        onPagination(e);
                    }}
                >
                    {tableColumns.map((column, index) =>
                        <Column
                            key={`gta-rates-column-${index}`}
                            field={column.field}
                            body={column.body}
                            header={column.label}
                            sortable={column.sortable}
                            sortField={column.sortField}
                            headerTooltip={column.headerTooltip}
                            editor={column.editor}
                            onCellEditComplete={column.onCellEditComplete}
                        />
                    )}
                </DataTable>
            </div>
        </StyledWrap>
    );
};

export default GtaRatesDatatable;
